import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import { apiClient } from './lib/apiClient';
import { formatPrice } from './lib/format';
import { useDriverAuth } from './hooks/useDriverAuth';
import { useDriverOrders } from './hooks/useDriverOrders';
import { useDriverToast } from './hooks/useDriverToast';
import DriverToastContainer from './components/DriverToastContainer';

import {
  DriverHeader,
  DriverStats,
  ActiveOrderCard,
  OrdersList,
  LocationIndicator,
} from './components';

function App() {
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [location, setLocation] = useState(null);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const fetchOrdersRef = useRef(null);

  const { toasts, toast } = useDriverToast();

  const {
    loggedIn,
    driver,
    token,
    loading,
    handleLogin,
    handleLogout,
    fetchDriverData,
    setDriver,
    setLoggedIn,
    setToken,
  } = useDriverAuth({
    phone,
    password,
    onLoginSuccess: (d) => fetchOrdersRef.current?.(d.id),
    onError: (msg) => toast.error(msg),
    onSuccess: (msg) => toast.success(msg),
  });

  const {
    pedidos: orders,
    pedidoActivo: currentOrder,
    fetchOrders,
    handleDriverAction,
  } = useDriverOrders({
    loggedIn,
    token,
    driver,
    audioEnabled,
    setLocation,
    onError: (msg) => toast.error(msg),
  });

  fetchOrdersRef.current = fetchOrders;

  const getStatusColor = (status) => {
    const colors = {
      'disponible': '#22C55E',
      'ocupado': '#f39c12',
      'offline': '#95A5A6'
    };
    return colors[status] || '#95A5A6';
  };

  const getStatusIcon = (status) => {
    const icons = {
      'disponible': '🟢',
      'ocupado': '🟠',
      'offline': '⚫'
    };
    return icons[status] || '⚫';
  };

  const getOrderStatusColor = (status) => {
    const colors = {
      'ready': '#FFC107',
      'in_route': '#2196F3',
      'delivered': '#22C55E',
      'received': '#9C27B0',
      'preparing': '#FF9800'
    };
    return colors[status] || '#757575';
  };

  const formatCurrency = (cents) => formatPrice((Number(cents) || 0) / 100);

  useEffect(() => {
    const enableAudio = () => {
      setAudioEnabled(true);
      window.removeEventListener('click', enableAudio);
    };
    window.addEventListener('click', enableAudio);
    return () => {
      window.removeEventListener('click', enableAudio);
    };
  }, []);

  const toggleState = async (newState) => {
    if (!token) {
      toast.error('Error: No hay sesión activa. Por favor, inicia sesión nuevamente.');
      return;
    }

    let currentDriver = driver;

    if (!currentDriver || !currentDriver.id) {
      console.log('⚠️ Driver no encontrado en estado, cargando desde servidor...');
      try {
        const response = await apiClient.get('/drivers/');
        const list = Array.isArray(response.data)
          ? response.data
          : (response.data?.results || response.data?.data || []);
        if (list && list.length > 0) {
          currentDriver = list[0];
          setDriver(currentDriver);
          console.log('✅ Driver cargado:', currentDriver);
        } else {
          toast.error('Error: No se encontró información del repartidor. Por favor, recarga la página.');
          return;
        }
      } catch (error) {
        console.error('Error al cargar driver:', error);
        if (error.response?.status === 401) {
          toast.error('Sesión expirada. Por favor, inicia sesión nuevamente.');
          setLoggedIn(false);
          setToken(null);
          localStorage.removeItem('driver_token');
          localStorage.removeItem('driver_refresh_token');
        } else {
          toast.error('Error al cargar información del repartidor. Por favor, recarga la página.');
        }
        return;
      }
    }

    await changeDriverState(currentDriver, newState);
  };

  const changeDriverState = async (driverToUpdate, newState) => {
    if (!driverToUpdate || !driverToUpdate.id) {
      toast.warning('Error: No se encontró información del repartidor');
      return;
    }

    try {
      console.log(`🔄 Cambiando estado a: ${newState}`);
      const response = await apiClient.post(
        `/drivers/${driverToUpdate.id}/toggle-state/`,
        { state: newState }
      );

      console.log('✅ Estado cambiado:', response.data);
      const updatedDriver = { ...driverToUpdate, state: response.data.state };
      setDriver(updatedDriver);

      if (token) {
        fetchDriverData();
      }
    } catch (error) {
      console.error('❌ Error al cambiar estado:', error);

      if (error.response?.status === 401) {
        toast.error('Sesión expirada. Por favor, inicia sesión nuevamente.');
        setLoggedIn(false);
        setToken(null);
        localStorage.removeItem('driver_token');
        localStorage.removeItem('driver_refresh_token');
      } else {
        const errorMessage = error.response?.data?.error ||
          error.response?.data?.detail ||
          error.message ||
          'Error desconocido al cambiar estado';
        toast.error(`Error al cambiar estado: ${errorMessage}`);
      }
    }
  };

  const getRating = () => {
    if (!driver || driver.rating === null || driver.rating === undefined) {
      return 5.0;
    }
    if (typeof driver.rating === 'number') {
      return isNaN(driver.rating) || !isFinite(driver.rating) ? 5.0 : driver.rating;
    }
    if (typeof driver.rating === 'string') {
      const parsed = parseFloat(driver.rating);
      return isNaN(parsed) || !isFinite(parsed) ? 5.0 : parsed;
    }
    const converted = Number(driver.rating);
    return isNaN(converted) || !isFinite(converted) ? 5.0 : converted;
  };

  const stats = {
    totalDeliveries: driver?.total_deliveries || 0,
    rating: getRating(),
    activeOrders: orders.filter(o => o.status === 'in_route' || o.status === 'ready').length
  };

  if (loading) {
    return (
      <div className="app login-screen">
        <div className="login-container">
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏳</div>
            <h2>Verificando sesión...</h2>
            <p style={{ color: '#666', marginTop: '10px' }}>Por favor espera</p>
          </div>
        </div>
      </div>
    );
  }

  if (!loggedIn) {
    return (
      <div className="app login-screen">
        <div className="login-container">
          <div className="login-header">
            <div className="logo-container" style={{ display: 'flex', justifyContent: 'center', marginBottom: '10px' }}>
              <img
                src="/logo.png"
                alt="Llega Logo"
                style={{ height: '45px', width: 'auto', maxWidth: '160px' }}
              />
            </div>
            <h1>Llega</h1>
            <p className="subtitle">Repartidor</p>
          </div>
          <form onSubmit={handleLogin} className="login-form">
            <div className="input-group">
              <span className="input-icon">📱</span>
              <input
                type="tel"
                placeholder="Teléfono"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
              />
            </div>
            <div className="input-group">
              <span className="input-icon">🔒</span>
              <input
                type="password"
                placeholder="Contraseña"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <button type="submit" className="login-button">
              Iniciar Sesión
            </button>
          </form>
        </div>
        <DriverToastContainer toasts={toasts} />
      </div>
    );
  }

  return (
    <div className="app">
      <div className="app-container">
        <DriverHeader driver={driver} onLogout={handleLogout} />

        {/* Status Toggle - se mantiene en App.js */}
        <div className="status-section">
        {!driver ? (
          <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
            <p>⏳ Cargando información del repartidor...</p>
          </div>
        ) : (
          <>
            <div className="status-indicator" style={{ backgroundColor: getStatusColor(driver?.state) }}>
              <span className="status-icon">{getStatusIcon(driver?.state)}</span>
              <span className="status-text">{driver?.state?.charAt(0).toUpperCase() + driver?.state?.slice(1) || 'Offline'}</span>
            </div>
            <div className="status-buttons">
              <button
                onClick={() => toggleState('disponible')}
                disabled={!driver || !driver.id}
                className={`status-btn ${driver?.state === 'disponible' ? 'active' : ''}`}
                style={{
                  backgroundColor: driver?.state === 'disponible' ? '#22C55E' : '#fff',
                  color: driver?.state === 'disponible' ? '#fff' : '#333',
                  opacity: (!driver || !driver.id) ? 0.5 : 1,
                  cursor: (!driver || !driver.id) ? 'not-allowed' : 'pointer'
                }}
              >
                <span className="btn-icon">✅</span>
                Disponible
              </button>
              <button
                onClick={() => toggleState('ocupado')}
                disabled={!driver || !driver.id}
                className={`status-btn ${driver?.state === 'ocupado' ? 'active' : ''}`}
                style={{
                  backgroundColor: driver?.state === 'ocupado' ? '#f39c12' : '#fff',
                  color: driver?.state === 'ocupado' ? '#fff' : '#333',
                  opacity: (!driver || !driver.id) ? 0.5 : 1,
                  cursor: (!driver || !driver.id) ? 'not-allowed' : 'pointer'
                }}
              >
                <span className="btn-icon">⏸️</span>
                Ocupado
              </button>
              <button
                onClick={() => toggleState('offline')}
                disabled={!driver || !driver.id}
                className={`status-btn ${driver?.state === 'offline' ? 'active' : ''}`}
                style={{
                  backgroundColor: driver?.state === 'offline' ? '#95A5A6' : '#fff',
                  color: driver?.state === 'offline' ? '#fff' : '#333',
                  opacity: (!driver || !driver.id) ? 0.5 : 1,
                  cursor: (!driver || !driver.id) ? 'not-allowed' : 'pointer'
                }}
              >
                <span className="btn-icon">⏹️</span>
                Offline
              </button>
            </div>
          </>
        )}
        </div>

        <DriverStats
        totalDeliveries={stats.totalDeliveries}
        rating={stats.rating}
        activeOrders={stats.activeOrders}
        />

        <ActiveOrderCard
        order={currentOrder}
        driverLocation={location}
        onDriverAction={handleDriverAction}
        getOrderStatusColor={getOrderStatusColor}
        formatCurrency={formatCurrency}
        />

        <OrdersList
        orders={orders}
        onDriverAction={handleDriverAction}
        getOrderStatusColor={getOrderStatusColor}
        formatCurrency={formatCurrency}
        />

        <LocationIndicator location={location} />
      </div>
      <DriverToastContainer toasts={toasts} />
    </div>
  );
}

export default App;
